/**
 * Subscribes to Supabase auth state and exposes the current session as a
 * React Query cache entry.
 *
 * Why cache auth state in React Query (not just Context)?
 * - Co-located with every other piece of server state in the app.
 * - Other hooks can declare `enabled: !!session` without prop-drilling.
 * - onAuthStateChange only needs one subscriber (this hook) regardless of
 *   how many components depend on the session.
 *
 * The subscription is set up once at module load; the hook itself is a
 * thin `useQuery` against the cached value. React Query dedupes the
 * initial `getSession()` call and replays cached results instantly after
 * the first render.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";
import { useEffect } from "react";

import { supabase } from "@/lib/supabaseClient";

const sessionKey = ["auth", "session"] as const;

async function fetchSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session ?? null;
}

export function useSession() {
  const qc = useQueryClient();

  // Keep the cache in sync with Supabase's auth events. Installing the
  // subscription inside the hook means it's tied to the provider's
  // lifetime — in practice a single provider at the app root.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      qc.setQueryData<Session | null>(sessionKey, session ?? null);
      // Role cache depends on the session; drop it on sign-in/out so the
      // next read re-fetches against the right user.
      qc.invalidateQueries({ queryKey: ["auth", "roles"] });
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  return useQuery<Session | null>({
    queryKey: sessionKey,
    queryFn: fetchSession,
    // Auth state is pushed via onAuthStateChange; we don't need refetches.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
